import { Header } from 'ui/components/4-habitats/Header';
import { useNavigationContext } from 'application/adapters/context/NavigationContext';
import { SiteContext } from 'application/adapters/context/SiteContext';
import { useContext, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { currency, resetCart, setCart } from 'application/adapters/store/slices/cartSlice';
import { currentCustomer, setCustomer } from 'application/adapters/store/slices/customerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { getUserBasket } from 'application/repositories/ecommerceRepository';
import { signIn, signOut } from 'next-auth/react';
import { useAuthenticationContext } from 'application/contexts/AuthenticationContext';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { useFormContext } from 'application/adapters/context/FormContext';
import { useContactLinkQuery } from 'application/hooks/useContactLinkQuery';
import { getBusinessPartners } from 'application/repositories/businessPartnersRepository';
import { setCulture } from 'application/adapters/store/slices/cultureSlice';
import { setCookie } from 'cookies-next';
import { LocaleContext } from 'application/adapters/context/LocaleContext';
import formatCartPrices from 'helpers/formatCartPrices';
import { normalizeToHash } from 'helpers/modalSignupHashUtils';

// Coupled to the exact CMS node names/titles set up in Umbraco for these two Service Navigation items
// (ContentCMS/.../uSync/v13/Content/struers-smartlab.config, support.config - Name/navigationTitle "Struers SmartLab" and "Support").
// If a content editor renames either node, it will stop being matched here and disappear for all users
// (including privileged portal users) rather than becoming visible to everyone - fails closed, not open.
// Each item requires its own privilege set, per client confirmation on #306238 - see authRepository.ts.
const PORTAL_PRIVILEGE_GATED_SERVICE_NAV_ITEM_NAMES = ['struers smartlab', 'support'];

interface HeaderFeatureProps {
	accessToken?: any;
	basketUrl: string;
	culture: string;
	contactLink?: Umbraco.Link;
	continents?: Umbraco.Continent[];
	languages?: LanguageSelectorNode;
}
type LanguageSelectorNode = {
	continents?: Umbraco.Continent[];
	properties?: { continents?: Umbraco.Continent[] };
};

export const HeaderFeature: React.FC<HeaderFeatureProps> = ({
	accessToken,
	basketUrl,
	culture,
	contactLink,
	languages,
	continents: continentsProp,
	...rest
}) => {
	const continents = useMemo(() => {
		if (Array.isArray(continentsProp) && continentsProp.length) return continentsProp;
		return languages?.continents ?? languages?.properties?.continents ?? [];
	}, [continentsProp, languages]);

	const dictionary = useContext(DictionaryContext);
	const navContext = useNavigationContext();
	useContext(SiteContext);
	const router = useRouter();
	const authentication = useAuthenticationContext();
	const formContext = useFormContext();
	const navigationItems = navContext?.getNavigationVisible().decendants;
	const serviceNavigationItems =
		navContext?.getNavigationVisible({
			byName: 'service',
		}).decendants ?? [];

	// "Struers SmartLab" and "Support" are plain, unrestricted CMS nav nodes - gating happens here.
	// `access` is computed server-side, in the NextAuth jwt callback (pages/api/auth/[...nextauth].ts).
	const serviceNavItemAccessByName: Record<string, boolean> = {
		'struers smartlab': !!authentication?.access.smartLab,
		support: !!authentication?.access.support,
	};

	const visibleServiceNavigationItems = serviceNavigationItems.filter((item) => {
		const label = (item.title || item.name || '').toLowerCase();
		return !PORTAL_PRIVILEGE_GATED_SERVICE_NAV_ITEM_NAMES.includes(label) || serviceNavItemAccessByName[label];
	});

	const dispatch = useDispatch();
	const businessPartner = useSelector(currentCustomer);
	const href = `/${router.locale}`;
	const localeContext = useContext(LocaleContext);
	const currencyCode = useSelector(currency);
	const viewCartDisabledPaths = ['/delivery', '/information', '/payment', '/receipt'];
	const enableViewCart = !viewCartDisabledPaths.includes(router.asPath.toLowerCase());

	useEffect(() => {
		const bpId = businessPartner?.id;

		if (accessToken && bpId && culture) {
			getUserBasket(accessToken, culture, bpId).then((res) => {
				if (res) {
					dispatch(setCart(formatCartPrices(res, localeContext, currencyCode)));
				}
			});
		} else {
			dispatch(resetCart());
		}
	}, [accessToken, businessPartner, culture, dispatch]);

	useEffect(() => {
		dispatch(setCulture(culture));
	}, [culture, dispatch]);

	function logoClicked(e): void {
		e.preventDefault();
		router.push(`/${router.locale}`);
	}
	// Fetch businessPartners if user is authenticated but no businessPartner is set
	useEffect(() => {
		if (accessToken && !businessPartner?.id) {
			getBusinessPartners(accessToken)
				.then((res) => {
					// Pre-select businessPartner if there's only one
					let countryCode = 'DK';

					if (res?.length === 1) {
						const customer = res[0];
						dispatch(setCustomer(customer));
						setCookie('bpId', customer.id);
						countryCode = customer.addresses?.find((address) => address.key === 'shipTo')?.value.country.trim();
					}
					// Turn this off until Specs for which logged in user should have auto-selected address are more CLEAR.
					// Pre-select businessPartner if there's only one or businessPartner has no addresses
					// const setCustomerAndCountry = (customer: Models.BusinessPartner) => {
					// 	dispatch(setCustomer(customer));
					// 	setCookie('bpId', customer.id);
					// 	const soldToCountry = customer.addresses?.find((address) => address.key === 'soldTo')?.value.country?.trim();
					// 	if (soldToCountry) {
					// 		countryCode = soldToCountry;
					// 	}
					// };

					// if (res?.length === 1) {
					// 	// Preselect if only one business partner
					// 	setCustomerAndCountry(res[0]);
					// } else if (res?.length > 1) {
					// 	// Multiple business partners, but no addresses on current bp
					// 	setCustomerAndCountry(res[0]);
					// }

					if (!localeContext.getLocaleCountry()) {
						localeContext.setLocaleCountry(countryCode);
					}
				})
				.catch((err) => {
					console.error(err);
				});
		} else if (!accessToken) {
			dispatch(setCustomer(null));
			setCookie('bpId', '');
		}
	}, [accessToken]);

	// Search
	const handleSearchSubmit = (query: string) => {
		const locale = router.locale ? `${router.locale}/` : '';
		router.push(`/${locale}search?query=${query}&page=1`);
	};
	const [isLoading, setIsLoading] = useState(false);
	async function handleSignOut() {
		setIsLoading(true);
		signOut({
			redirect: false,
		}).then(() => {
			const pagesToRedirectToHome = ['/my-account', '/basket', '/delivery', '/information', '/payment', '/receipt'];
			const redirectToHome = pagesToRedirectToHome.reduce((acc, el) => acc || router.asPath.startsWith(el), false);
			const returnUrl = redirectToHome ? location.origin : location.href;
			router.push(`https://${process.env.NEXT_PUBLIC_AUTHENTICATION_DOMAIN}/account/logout?returnUrl=${returnUrl}`);
		});
	}

	const handleGoToCheckout = basketUrl
		? () => {
				router.push(basketUrl);
		  }
		: null;

	const onNavigateToUserProfile = () => {
		router.push('/my-account');
	};

	const loginStatusDictionary = {
		firstButtonText: dictionary.getValue('LoginStatus.FirstButtonText', null, 'User details'),
		secondButtonText: dictionary.getValue('LoginStatus.SecondButtonText', null, 'Logout'),
		loggedInAs: dictionary.getValue('LoginStatus.LoggedInAs', null, 'Logged in as'),
		registeredAddresses: dictionary.getValue('LoginStatus.RegisteredAddresses', null, 'Your registered addresses'),
		soldToAddress: dictionary.getValue('LoginStatus.SoldToAddress', null, 'Sold to address'),
		shippingAddress: dictionary.getValue('LoginStatus.ShippingAddress', null, 'Shipping address'),
		invoiceAddress: dictionary.getValue('LoginStatus.InvoiceAddress', null, 'Invoice address'),
		missingCustomerProfileMessage: dictionary.getValue(
			'MyAccount.MissingCustomerProfileMessage',
			null,
			'No customer profile selected. Click here to choose one.',
		),
		missingAddressesUrl: '/my-account/delivery-addresses',
		links: dictionary.getValue('LoginStatus.Links', null, 'Links'),
		change: dictionary.getValue('Action.Change', null, 'Change'),
		customerDetails: dictionary.getValue('LoginStatus.CustomerDetails', null, 'Customer details'),
		changeCallAccount: dictionary.getValue('LoginStatus.ChangeCallAccount', null, 'Change call account'),
		soldTo: dictionary.getValue('Action.SoldTo', null, 'Sold to'),
		shipTo: dictionary.getValue('Action.ShipTo', null, 'Ship to'),
		invoiceTo: dictionary.getValue('Action.InvoiceTo', null, 'Invoice to'),
	};

	const loginStatusLinks = [
		{
			title: dictionary.getValue('MyAccount', null, 'My account'),
			url: '/my-account',
		},
		{
			title: dictionary.getValue('DeliveryAddresses', null, 'Delivery addresses'),
			url: '/my-account/delivery-addresses',
		},
		{
			title: dictionary.getValue('OrderHistory', null, 'Order history'),
			url: '/my-account/order-history',
		},
	] as Models.NavigationItem[];

	// Extract query param and buildUrlWithParams helper from hook
	const { buildUrlWithParams } = useContactLinkQuery({ contactLink });
	const queryParam = contactLink?.url?.split('?')[1]?.split('#')[0] || 'contact=show';

	const handleContactForm = () => {
		// Build URL with contact param while preserving existing UTM params
		const queryString = buildUrlWithParams(true);
		const newUrl = `${window.location.pathname}${queryString ? '?' + queryString : ''}${window.location.hash}`;
		window.history.pushState(null, '', newUrl);
		formContext.open();
	};

	/* Sign up (Request account) form */
	const signupFormLinkText = !accessToken ? dictionary.getValue('SignupForm.LinkText', null, 'Request Account') : null;
	const signupFormHash = signupFormLinkText ? normalizeToHash(signupFormLinkText) : null;

	let allServiceNavItems: Navigation.NestedNavigationItem[];
	if (accessToken) {
		allServiceNavItems = visibleServiceNavigationItems;
	} else if (signupFormLinkText && visibleServiceNavigationItems.findIndex((item) => item.name === signupFormLinkText) === -1) {
		allServiceNavItems = [{ name: signupFormLinkText, url: '#' + signupFormHash }];
	} else {
		allServiceNavItems = [];
	}

	const serviceNavigationButtonItems = allServiceNavItems.filter((item) => item.showAsButton);
	const serviceNavigationLinkItems = allServiceNavItems.filter((item) => !item.showAsButton);

	const rearrangedServiceNavigationItems = [...serviceNavigationLinkItems, ...serviceNavigationButtonItems];

	const headerDictionary = {
		skipToContent: dictionary.getValue('Header.SkipToContent', null, 'Skip to content'),
		moreLanguagesText: dictionary.getValue('Header.MoreLanguagesText', null, 'More languages'),
	};

	const avatarDictionary = {
		actionLogin: dictionary.getValue('Action.Login', null, 'Login'),
	};

	return (
		<>
			<Header
				navigationItems={navigationItems}
				serviceNavigationItems={rearrangedServiceNavigationItems}
				logoClicked={logoClicked}
				href={href}
				onSignIn={() => {
					signIn('authServer');
				}}
				disabled={isLoading}
				onSignOut={handleSignOut}
				profile={authentication?.user}
				accessToken={accessToken}
				handleSearchSubmit={handleSearchSubmit}
				handleGoToCheckout={handleGoToCheckout}
				handleContactForm={handleContactForm}
				contactFormLoading={formContext.isLoading}
				onNavigateToUserProfile={onNavigateToUserProfile}
				loginStatusDictionary={loginStatusDictionary}
				avatarDictionary={avatarDictionary}
				addresses={businessPartner?.addresses}
				bpId={businessPartner?.id}
				links={loginStatusLinks}
				dictionary={headerDictionary}
				enableViewCart={enableViewCart}
				continents={continents}
				contactLink={contactLink}
			/>
		</>
	);
};
